/**
 * API client (axios).
 * - Auto-Idempotency-Key для всех мутаций.
 * - 401 → authStore.markExpired() → <AuthExpiredModal> в <Shell>.
 *   НЕ делаем автоматический retry, НЕ делаем window.location.href redirect.
 * - SSE идёт напрямую на VITE_API_BASE (минуя Vite proxy), чтобы избежать буферизации.
 *
 * Phase 2.6 (Session/Auth UX):
 * - 401 → markExpired() (один раз: повторные 401 на уже-expired store no-op).
 * - Если на текущей странице уже /login → НЕ выставляем expired (пользователь
 *   пытается войти, модалка ему не нужна).
 */
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { useAuthStore } from '@/store/authStore';

const BASE_URL = import.meta.env.VITE_API_BASE || 'http://localhost:8294/api/v1';

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
  timeout: 60_000,
});

// === REQUEST INTERCEPTOR ===
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Idempotency key для мутаций
  if (config.method && ['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers.set('X-Idempotency-Key', uuidv4());
  }

  // JWT из localStorage
  const token = localStorage.getItem('cs_access_token');
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

// === RESPONSE INTERCEPTOR ===
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Не показываем модалку если пользователь уже на /login (форма логина).
      // Это предотвращает бесконечный loop при вводе неверного пароля.
      const onLogin = window.location.pathname.startsWith('/login');
      if (!onLogin) {
        const { expired, markExpired } = useAuthStore.getState();
        // markExpired идемпотентен: если уже expired — no-op (не дёргаем
        // localStorage.removeItem, не триггерим re-render). Это даёт ОДНУ
        // AuthExpiredModal даже при N параллельных 401.
        if (!expired) {
          markExpired();
        }
      }
    }
    return Promise.reject(error);
  },
);

// === DEV BYPASS ===
// Если есть VITE_DEV_AUTH_BYPASS_TOKEN — клиент автоматически использует его.
// Сервер проверяет наличие такого же токена в ENV и пускает как superadmin.
const DEV_TOKEN = import.meta.env.VITE_DEV_AUTH_BYPASS_TOKEN;
if (DEV_TOKEN) {
  api.defaults.headers.common['X-Dev-Bypass-Token'] = DEV_TOKEN;
}

export default api;
